import { createSelector } from "reselect";
import type { FileInfo } from "@exagens/behavioral-insight-console-client";
import type { RootState } from "@/store/app/storeTypes";
import { catalogFingerprint } from "@/features/sparxsEditor/lib/catalog/catalogFingerprint";
import { selectModifiedFileNames } from "@/features/sparxsEditor/store/slices/session/sessionSlice";

const selectSparxsListData = (state: RootState) => state.editor.sparxsList.data;

const selectSparxsListExcludedData = (state: RootState) =>
  state.editor.sparxsList.excludedData;

export const selectCatalogFingerprint = createSelector(
  [selectSparxsListData, selectSparxsListExcludedData],
  (data, excluded) => catalogFingerprint(data, excluded),
);

const selectBlueprintFetchFailuresByFileName = (state: RootState) =>
  state.editor.sparxsBlueprintFetchFailures.byFileName;

const selectEffectiveCatalogFileNames = createSelector(
  [selectSparxsListData, selectBlueprintFetchFailuresByFileName],
  (data, failuresBy) => {
    const failed = new Set(Object.keys(failuresBy));
    return data
      .map((f) => f.fileName)
      .filter((fileName) => !failed.has(fileName));
  },
);

export const selectCatalogScopedModifiedFileNames = createSelector(
  [selectModifiedFileNames, selectEffectiveCatalogFileNames],
  (modified, effectiveCatalog) => {
    const effective = new Set(effectiveCatalog);
    return modified.filter((fileName) => effective.has(fileName));
  },
);

const selectBlueprintFetchFailedFileNames = createSelector(
  [selectBlueprintFetchFailuresByFileName],
  (byFileName) => Object.keys(byFileName),
);

export const selectHasBlueprintFetchFailures = createSelector(
  [selectBlueprintFetchFailedFileNames],
  (names) => names.length > 0,
);

export const selectSparxsListDataWithoutFetchFailures = createSelector(
  [selectSparxsListData, selectBlueprintFetchFailuresByFileName],
  (data, failuresBy): FileInfo[] => {
    const failed = new Set(Object.keys(failuresBy));
    return data.filter((f) => !failed.has(f.fileName));
  },
);
